import { CancelablePromise } from "../cancelablePromise";
import * as logger from "../logger";
import { AppType, getAppType } from "./nativeHelpers";

/**
 * api.mobile.bottomBar: an add-in shows or hides the native bottom navigation bar on its own page.
 *
 * Native keeps one request, and shows the bar only while an add-in page is current. This module is what makes
 * the request belong to one add-in page:
 *
 *   - only the add-in whose page is current can make it, matched by add-in name, not by view (an add-in on
 *     another page, or one that has left, gets `false` and nothing reaches native). A delayed call from the
 *     same add-in is therefore taken on its next visit, so an add-in cancels visit-specific async work in blur();
 *   - every add-in view that becomes active starts hidden, so a previous add-in's request never carries over;
 *   - the request is cleared when the focused view is removed. The router removes the old view before it builds
 *     its replacement, so the old view's blur() always runs first.
 *
 * Native also clears it when the WebView leaves an add-in page, on logout and on a viewer switch.
 */

/** How long to wait for native before telling the add-in the bar was not applied. */
const NATIVE_TIMEOUT_MS = 2000;

interface IActiveAddinPage {
    /** The view object; identity decides whose removal may clear, so removing a replaced view keeps its successor's request. */
    view: object;
    name: string;
}

let activePage: IActiveAddinPage | null = null;

/** True on a DriveNative build that ships the bottom bar bridge. api.mobile.bottomBar exists only then. */
export function isAddinBottomBarSupported(): boolean {
    return getAppType() === AppType.NativeSdk && typeof window.geotabModules?.addinBottomBar?.set === "function";
}

function send(visible: boolean): CancelablePromise<boolean> {
    return new CancelablePromise<boolean>(resolve => {
        let settled = false;
        const settle = (applied: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            resolve(applied);
        };
        const timer = window.setTimeout(() => {
            logger.log(logger.Source.NATIVE, "addinBottomBar: native did not respond");
            settle(false);
        }, NATIVE_TIMEOUT_MS);
        try {
            window.geotabModules?.addinBottomBar?.set({ visible }, (error?: Error, applied?: boolean) => {
                window.clearTimeout(timer);
                if (error) {
                    logger.error(logger.Source.NATIVE, `addinBottomBar: ${ error.message }`);
                    settle(false);
                    return;
                }
                settle(applied === true);
            });
        } catch (error) {
            window.clearTimeout(timer);
            logger.error(logger.Source.NATIVE, `addinBottomBar threw: ${ (error as Error).message }`);
            settle(false);
        }
    });
}

/** The view of an add-in page became the focused one. Call before the add-in's own focus() runs. */
export function activateAddinPage(view: object, name: string): void {
    activePage = { view, name };
    if (isAddinBottomBarSupported()) {
        void send(false);
    }
}

/** The view is being removed. Only the focused view clears, so removing a replaced view keeps its successor's request. */
export function deactivateAddinPage(view: object): void {
    if (activePage?.view !== view) {
        return;
    }
    activePage = null;
    if (isAddinBottomBarSupported()) {
        void send(false);
    }
}

/**
 * Show or hide the bar for `addinName`. Resolves `true` when native took the request, `false` when it did not:
 * the add-in's page is not the focused one, this host has no bar to show (native navigation is off), or native
 * did not answer. Never rejects.
 */
export function setAddinBottomBar(addinName: string, visible: boolean): CancelablePromise<boolean> {
    if (!isAddinBottomBarSupported() || activePage?.name !== addinName) {
        return CancelablePromise.resolve(false);
    }
    return send(visible);
}
